import { useCallback, useEffect, useId, useRef, useState } from 'react';

import type { Breadcrumb, DonationForm, IPredicate } from '@feathr/blackbox';
import type { ISorted } from '@feathr/components';
import { useStore } from '@feathr/extender/state';
import type { ListResponse } from '@feathr/rachis';

import type { IDonationCreatedEvent } from '../../hooks';
import {
  breadcrumbToTransactionEvent,
  DONATION_LIST_PARAMS,
  formatPersonAddress,
} from './TransactionTable.utils';

/**
 * Maps table column IDs to API field paths for server-side sorting.
 * Only includes fields that are sortable server-side (direct Breadcrumb fields).
 * Fields requiring joins (e.g., person name) are not sortable server-side.
 */
const SORT_FIELD_MAP: Record<string, string> = {
  time: 'd_c',
  amount: 'donation.amount',
  credits_earned: 'growth_credits',
  donation_type: 'donation.frequency',
  // form_name is not sortable server-side (requires join to DonationForms)
};

/** Default sort: most recent donations first */
const DEFAULT_SORT: ISorted[] = [{ id: 'time', desc: true }];

interface IUseHistoricalDonationsReturn {
  /** Transformed donation events from historical breadcrumbs */
  donations: IDonationCreatedEvent[];
  /** Whether historical data is loading */
  isLoading: boolean;
  /** Whether there was an error loading data */
  isErrored: boolean;
  /**
   * Current page index (0-based). Matches both the Breadcrumbs API and the
   * Table component conventions, so it can be passed directly to either.
   */
  page: number;
  /** Total number of pages */
  totalPages: number;
  /** Number of items per page */
  pageSize: number;
  /** Current sort configuration */
  sort: ISorted[];
  /** Go to next page */
  nextPage: () => void;
  /** Go to previous page */
  prevPage: () => void;
  /** Go to specific page (0-based) */
  goToPage: (page: number) => void;
  /** Change page size (resets to first page) */
  setPageSize: (newPageSize: number) => void;
  /** Change sort order (triggers server-side re-fetch) */
  onSortChange: (newSort: ISorted[]) => void;
  /** Refresh the data */
  refresh: () => void;
}

const DEFAULT_PAGE_SIZE = 10;

/**
 * Hook for fetching and transforming historical donation breadcrumbs.
 *
 * IMPORTANT: The component using this hook MUST be wrapped with observer()
 * from mobx-react-lite for proper reactivity.
 *
 * @param initialPageSize - Initial number of items per page (default: 10). Can be changed at runtime via the returned `setPageSize` callback.
 * @param additionalPredicates - Additional predicates to merge with the base donation predicate
 * @returns Historical donations data with pagination controls
 *
 * @example
 * ```tsx
 * const TransactionTable = observer(function TransactionTable() {
 *   const { donations, isLoading, page, totalPages, nextPage, prevPage } =
 *     useHistoricalDonations(10);
 *
 *   if (isLoading) return <Spinner />;
 *   return <Table items={donations} />;
 * });
 * ```
 */
function useHistoricalDonations(
  initialPageSize = DEFAULT_PAGE_SIZE,
  additionalPredicates: IPredicate[] = [],
): IUseHistoricalDonationsReturn {
  const { Breadcrumbs, Persons, DonationForms } = useStore();
  const baseListId = useId();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSizeState] = useState(initialPageSize);
  const [sort, setSort] = useState<ISorted[]>(DEFAULT_SORT);
  const [breadcrumbsResponse, setBreadcrumbsResponse] = useState<ListResponse<Breadcrumb> | null>(
    null,
  );
  const [donationFormsResponse, setDonationFormsResponse] =
    useState<ListResponse<DonationForm> | null>(null);

  // Fetch donation forms for form names
  useEffect(() => {
    const formsResponse = DonationForms.list({ pagination: { page_size: 1000 } });
    setDonationFormsResponse(formsResponse);
  }, [DonationForms]);

  // Convert sort state to API ordering parameter
  const ordering = sort
    .filter((s) => SORT_FIELD_MAP[s.id])
    .map((s) => `${s.desc ? '-' : ''}${SORT_FIELD_MAP[s.id]}`);

  // Create a stable key for predicates to detect changes
  const predicatesKey = JSON.stringify(additionalPredicates);

  // Include ordering, page, page size, and predicates in listId so each configuration gets its own cache entry
  const orderingKey = ordering.length > 0 ? ordering.join(',') : '-d_c';
  const listId = `${baseListId}-${orderingKey}-page${page}-size${pageSize}-pred${predicatesKey}`;

  // Reset to first page when predicates change
  useEffect(() => {
    setPage(0);
  }, [predicatesKey]);

  // Force a server refetch when predicates change. Without this, rachis would
  // serve a stale cached response any time the user returns to a previously-used
  // predicate state (e.g. clearing all filters back to the empty default), so
  // donations that arrived via SSE while a different filter was active and were
  // skipped by the matchesFilters gate would stay missing until a hard refresh.
  const lastFetchedPredicatesKey = useRef<string | null>(null);

  // Fetch breadcrumbs with pagination and sorting
  useEffect(() => {
    const isPredicateChange =
      lastFetchedPredicatesKey.current !== null &&
      lastFetchedPredicatesKey.current !== predicatesKey;
    lastFetchedPredicatesKey.current = predicatesKey;

    const mergedPredicates = [...DONATION_LIST_PARAMS.predicates!, ...additionalPredicates];

    const response = Breadcrumbs.list(
      {
        ...DONATION_LIST_PARAMS,
        predicates: mergedPredicates,
        pagination: {
          page,
          page_size: pageSize,
        },
        ordering: ordering.length > 0 ? ordering : ['-d_c'],
      },
      { reset: isPredicateChange },
      listId,
    );
    setBreadcrumbsResponse(response);
  }, [Breadcrumbs, listId, page, pageSize, predicatesKey]);

  // Access MobX observable properties directly (not in useMemo)
  // This is required for observer() to detect changes and trigger re-renders
  const hasLoadedOnce = breadcrumbsResponse?.hasLoadedOnce ?? false;
  const isPending = breadcrumbsResponse?.isPending ?? true;
  const isErrored = breadcrumbsResponse?.isErrored ?? false;
  const models = breadcrumbsResponse?.models ?? [];
  const paginationCount = breadcrumbsResponse?.pagination?.count ?? 0;

  const formsLoaded = donationFormsResponse?.hasLoadedOnce ?? false;
  const formModels = donationFormsResponse?.models ?? [];

  // Transform breadcrumbs to donation events
  // NOTE: We intentionally don't use useMemo here because we need MobX observer
  // to detect changes to the models array. The transformation is fast enough
  // that re-computing on every render is acceptable.
  let donations: IDonationCreatedEvent[] = [];
  if (hasLoadedOnce && !isPending) {
    donations = models.map((breadcrumb) => {
      const perId = breadcrumb.get('per_id');
      const formId = breadcrumb.get('form_id');

      // Get person name, phone, and address
      let personData = {
        name: 'Anonymous',
        phone: '',
        address: '',
        isPending: false,
        isErrored: false,
      };
      if (perId) {
        const person = Persons.get(perId);
        personData = {
          name: person.name,
          phone: person.get('phone', ''),
          address: formatPersonAddress(person.get('address')),
          isPending: person.isPending,
          isErrored: person.isErrored,
        };
      }

      // Get form name
      let formName = 'Unknown Form';
      if (formId && formsLoaded) {
        const form = formModels.find((f) => f.get('id') === formId);
        formName = form?.get('name') || 'Unknown Form';
      }

      return breadcrumbToTransactionEvent(breadcrumb, personData, formName);
    });
  }

  // Calculate pagination info
  const totalPages = paginationCount > 0 ? Math.ceil(paginationCount / pageSize) : 1;

  const isLoading = !hasLoadedOnce || isPending;

  // totalPages is a count (e.g. 19 means pages 0..18 are valid).
  // NOTE: derived from the last settled response, so there's a brief window
  // between a setPageSize call (which resets page to 0) and the new response
  // landing where these bounds are stale. The Table is in its loading state
  // during that window, so the user can't trigger a navigation that would hit
  // the stale clamp.
  const lastPageIndex = Math.max(0, totalPages - 1);

  const nextPage = useCallback(() => {
    setPage((prev) => Math.min(prev + 1, lastPageIndex));
  }, [lastPageIndex]);

  const prevPage = useCallback(() => {
    setPage((prev) => Math.max(prev - 1, 0));
  }, []);

  const goToPage = useCallback(
    (newPage: number) => {
      setPage(Math.max(0, Math.min(newPage, lastPageIndex)));
    },
    [lastPageIndex],
  );

  const refresh = useCallback(() => {
    Breadcrumbs.refreshApiCache();
  }, [Breadcrumbs]);

  const onSortChange = useCallback((newSort: ISorted[]) => {
    setSort(newSort);
    // Reset to first page when sort changes
    setPage(0);
  }, []);

  const setPageSize = useCallback((newPageSize: number) => {
    setPageSizeState(newPageSize);
    // Reset to first page when page size changes so the user sees the start of the data set
    setPage(0);
  }, []);

  return {
    donations,
    isLoading,
    isErrored,
    page,
    totalPages,
    pageSize,
    sort,
    nextPage,
    prevPage,
    goToPage,
    setPageSize,
    onSortChange,
    refresh,
  };
}

export default useHistoricalDonations;
export type { IUseHistoricalDonationsReturn };
